import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Code, Brain, Database, Microscope, Download, Linkedin, Github, Twitter, Zap, Layers, Box, GitBranch, Server, HardDrive, Cloud, ExternalLink } from 'lucide-react';
import { profileData, projects, aboutSection, technicalCapabilities } from '../mock/data';

const Home = () => {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = React.useState(tabFromUrl || 'professional');

  const getCategoryColor = (category) => {
    return category === 'Applied Research' 
      ? 'bg-amber-50 text-amber-700 border-amber-200' 
      : 'bg-cyan-50 text-cyan-700 border-cyan-200';
  };

  const getProjectIcon = (id) => {
    const icons = [Brain, Code, Database, Microscope];
    const Icon = icons[id - 1] || Code;
    return Icon;
  };

  // Split projects into professional work and projects/internships
  const professionalWork = projects.filter(p => 
    p.organization !== 'IIT Kharagpur' && 
    p.organization !== 'Personal Project' &&
    !p.role.includes('Intern') &&
    (p.role.includes('Lead') || p.role.includes('Engineer') || p.role.includes('Consultant'))
  );
  
  const projectsInternships = projects.filter(p => 
    p.organization === 'IIT Kharagpur' || 
    p.organization === 'Personal Project' ||
    p.role.includes('Thesis') || 
    p.role.includes('Intern')
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Hero Section */}
      <section id="hero" className="pt-20 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 px-4 sm:px-6 lg:px-12 relative overflow-hidden group">
        {/* Decorative background element */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-teal-50 rounded-full blur-3xl opacity-30 animate-float group-hover:bg-purple-100 transition-colors duration-700"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Left: Text Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-block px-4 py-1.5 bg-teal-50 border border-teal-200 rounded-full mb-4">
                  <span className="text-xs text-teal-700 font-medium tracking-wide">AI Engineer & Researcher</span>
                </div>
                
                <h1 className="font-serif text-5xl lg:text-6xl text-[#1A1A1A] leading-tight tracking-tight">
                  {profileData.name}
                </h1>
                <p className="text-xl lg:text-2xl text-teal-700 font-medium">
                  {profileData.title}
                </p>
              </div>

              <div className="h-1 bg-gradient-to-r from-teal-600 to-transparent w-24 rounded-full"></div>

              <p className="text-base lg:text-lg text-[#6B6B6B] leading-relaxed max-w-xl">
                {profileData.bio}
              </p>

              <div className="flex flex-col items-start gap-6 pt-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-sm hover:bg-teal-700 transition-all duration-300 font-medium shadow-sm hover:shadow-md"
                >
                  Get in touch <ArrowRight size={16} />
                </Link>
                
                {/* Social Media Links - Centered with button */}
                <div className="flex gap-6 items-center">
                  <a
                    href="https://www.linkedin.com/in/uddeshya-barnwal-b02510100/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 transition-all duration-300 hover:scale-110"
                    aria-label="LinkedIn"
                  >
                    <Linkedin size={24} strokeWidth={1.5} />
                  </a>
                  <a
                    href="https://github.com/buddeshya"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 transition-all duration-300 hover:scale-110"
                    aria-label="GitHub"
                  >
                    <Github size={24} strokeWidth={1.5} />
                  </a>
                  <a
                    href="https://x.com/BarnwalUddeshya"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 transition-all duration-300 hover:scale-110"
                    aria-label="Twitter"
                  >
                    <Twitter size={24} strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Portrait */}
            <div className="flex justify-center lg:justify-end">
              <div className="flex flex-col items-center gap-3">
                {/* Transparent portrait with organic blob frame - 30% larger */}
                <div className="w-[416px] h-[416px] lg:w-[585px] lg:h-[585px]">
                  <img
                    src={profileData.profileImage}
                    alt={profileData.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applied Research ↔ Engineering Section */}
      <section id="about" className="py-32 px-6 lg:px-12 bg-white border-y border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto">
          {/* Section Heading */}
          <div className="text-center mb-20">
            <h2 className="font-serif text-4xl lg:text-5xl text-[#1A1A1A] mb-6 tracking-tight">
              <span className="text-amber-600">Applied Research</span>{' '}
              <span className="text-teal-600">↔</span>{' '}
              <span className="text-cyan-600">Engineering</span>
            </h2>
            <p className="text-lg text-[#6B6B6B] max-w-2xl mx-auto leading-relaxed">
              {aboutSection.subtitle}
            </p>
          </div>

          {/* Photo, Narrative, Stats, and Resume Button */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-24 items-start">
            {/* Left: Photo - Larger */}
            <div className="flex justify-center lg:justify-end">
              <div className="w-96 h-96 lg:w-[450px] lg:h-[450px] rounded-sm overflow-hidden shadow-2xl">
                <img
                  src={aboutSection.image}
                  alt="Uddeshya Barnwal"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right: Narrative + Combined Credentials Card */}
            <div className="space-y-8">
              {/* Narrative */}
              <p className="text-base lg:text-lg text-[#6B6B6B] leading-relaxed">
                {aboutSection.narrative}
              </p>
              
              {/* Combined Credentials Card */}
              <div className="bg-gradient-to-br from-teal-50/40 to-cyan-50/30 border border-teal-100 rounded-lg p-6 space-y-5 hover:shadow-lg transition-shadow duration-300">
                {/* Experience Stats - Compact */}
                <div className="grid grid-cols-3 gap-4">
                  {aboutSection.stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="text-2xl lg:text-3xl font-bold text-[#1A1A1A] mb-1">
                        {stat.value}
                      </div>
                      <p className="text-[9px] text-[#6B6B6B] leading-tight">{stat.label.split(' — ')[0]}</p>
                    </div>
                  ))}
                </div>

                {/* Divider */}
                <div className="border-t border-teal-200"></div>

                {/* Education - Compact */}
                <div className="flex items-start gap-3">
                  <div className="w-1 h-10 bg-gradient-to-b from-teal-500 to-cyan-500 rounded-full flex-shrink-0 mt-1"></div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-[#1A1A1A] mb-1">Education</h3>
                    <p className="text-sm font-semibold text-[#1A1A1A] mb-1">
                      Indian Institute of Technology Kharagpur
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-[#4A4A4A]">
                      <span>M.Tech & B.Tech</span>
                      <span className="text-[#999999]">•</span>
                      <span>2020</span>
                    </div>
                  </div>
                </div>

                {/* Resume Button - Compact */}
                <div className="pt-1">
                  <a
                    href={aboutSection.resumeUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white rounded-sm hover:bg-teal-700 transition-all duration-300 font-medium shadow-sm hover:shadow-md text-sm"
                  >
                    Download Resume <Download size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Split Identity Block - Enhanced Design with Reduced Color Intensity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left: Applied Research - Reduced Intensity */}
            <div className="bg-gradient-to-br from-amber-50/40 to-orange-50/30 border border-amber-100 rounded-lg p-8 lg:p-10 space-y-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-2 h-20 bg-gradient-to-b from-amber-400 to-orange-400 rounded-full flex-shrink-0"></div>
                  <div>
                    <h3 className="font-serif text-3xl lg:text-4xl text-[#1A1A1A] mb-2 leading-tight">
                      {aboutSection.research.title}
                    </h3>
                    <p className="text-base text-amber-700/80 italic font-semibold">
                      {aboutSection.research.subtitle}
                    </p>
                  </div>
                </div>
              </div>

              <ul className="space-y-5">
                {aboutSection.research.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="text-amber-500/80 mt-1.5 flex-shrink-0 text-xl font-bold">→</span>
                    <span className="text-base text-[#4A4A4A] leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Engineering - Reduced Intensity */}
            <div className="bg-gradient-to-br from-cyan-50/40 to-blue-50/30 border border-cyan-100 rounded-lg p-8 lg:p-10 space-y-6 hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-2 h-20 bg-gradient-to-b from-cyan-500 to-blue-500 rounded-full flex-shrink-0"></div>
                  <div>
                    <h3 className="font-serif text-3xl lg:text-4xl text-[#1A1A1A] mb-2 leading-tight">
                      {aboutSection.engineering.title}
                    </h3>
                    <p className="text-base text-cyan-700/80 italic font-semibold">
                      {aboutSection.engineering.subtitle}
                    </p>
                  </div>
                </div>
              </div>

              <ul className="space-y-5">
                {aboutSection.engineering.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="text-cyan-600/80 mt-1.5 flex-shrink-0 text-xl font-bold">→</span>
                    <span className="text-base text-[#4A4A4A] leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Selected Work - Redesigned with Tabs */}
      <section id="work" className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 sm:mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#1A1A1A] mb-3 sm:mb-4 font-bold">
              Selected Work
            </h2>
            <p className="text-sm sm:text-base text-[#6B6B6B] mb-6 sm:mb-8">
              Projects spanning applied research and production
            </p>

            {/* Segmented Control / Tabs - Enhanced Design */}
            <div className="inline-flex border-2 border-[#E5E5E5] rounded-xl p-1 sm:p-1.5 bg-gray-50 shadow-sm w-full sm:w-auto">
              <button
                onClick={() => setActiveTab('professional')}
                className={`flex-1 sm:flex-none px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 ${
                  activeTab === 'professional'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-gray-100'
                }`}
              >
                Professional Work
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`flex-1 sm:flex-none px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 ${
                  activeTab === 'projects'
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-gray-100'
                }`}
              >
                Projects & Internships
              </button>
            </div>
          </div>

          {/* Professional Work Cards */}
          {activeTab === 'professional' && (
            <div className="space-y-4 sm:space-y-6">
              {professionalWork.map((project, index) => (
                <Link
                  key={project.id}
                  to={`/work/${project.slug}`}
                  className="group block border border-[#E5E5E5] hover:border-teal-200 rounded-lg p-4 sm:p-6 lg:p-8 bg-white hover:shadow-xl transition-all duration-300 relative"
                >
                  <article>
                    {/* View Details Indicator */}
                    <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                      <div className="flex items-center gap-1.5 text-teal-600">
                        <span className="text-xs font-medium hidden sm:inline">View Details</span>
                        <ArrowRight size={14} className="sm:group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>

                    {/* Number Indicator */}
                    <div className="flex items-start gap-3 sm:gap-4 lg:gap-6">
                      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-[#E5E5E5] group-hover:border-teal-600 flex items-center justify-center transition-colors duration-300">
                        <span className="text-base sm:text-lg font-bold text-[#6B6B6B] group-hover:text-teal-600 transition-colors duration-300">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                      </div>

                      <div className="flex-1 sm:pr-16 min-w-0">
                        {/* Project Title - Highest Priority */}
                        <h3 className="font-serif text-xl sm:text-2xl lg:text-3xl text-[#1A1A1A] mb-2 sm:mb-3 group-hover:text-teal-700 transition-colors duration-300 leading-tight">
                          {project.title}
                        </h3>

                        {/* Identity Label + Role + Company + Location */}
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                          <span className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border font-medium ${getCategoryColor(project.category)}`}>
                            {project.category}
                          </span>
                          <span className="text-xs sm:text-sm text-[#4A4A4A] break-words">
                            {project.role === project.organization ? project.organization : `${project.role} · ${project.organization}`}
                          </span>
                          {project.location && (
                            <>
                              <span className="text-[#999999] hidden sm:inline">·</span>
                              <span className="text-xs sm:text-sm text-[#999999] hidden sm:inline">
                                {project.location}
                              </span>
                            </>
                          )}
                          <span className="text-[#999999] hidden sm:inline">·</span>
                          <span className="text-xs sm:text-sm text-[#999999] hidden sm:inline">
                            {project.period}
                          </span>
                        </div>

                        {/* Impact-Focused Summary (1-2 lines max) */}
                        <p className="text-sm sm:text-base text-[#6B6B6B] leading-relaxed mb-4 sm:mb-6 line-clamp-2">
                          {project.description}
                        </p>

                        {/* Tags - Exactly 3, Lowest Priority */}
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {project.tags.slice(0, 3).map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-50 text-[#6B6B6B] rounded-md border border-gray-200 group-hover:border-teal-100 group-hover:bg-teal-50/30 transition-colors duration-300"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}

          {/* Projects & Internships Cards - Same Design as Professional Work */}
          {activeTab === 'projects' && (
            <div className="space-y-4 sm:space-y-6">
              {projectsInternships.map((project, index) => {
                // Check if project has detail page (fullDescription exists)
                const hasDetailPage = project.fullDescription;
                const CardWrapper = hasDetailPage ? Link : 'div';
                const cardProps = hasDetailPage 
                  ? { to: `/work/${project.slug}` }
                  : {};

                return (
                  <CardWrapper
                    key={project.id}
                    {...cardProps}
                    className={`group block border border-[#E5E5E5] hover:border-teal-200 rounded-lg p-4 sm:p-6 lg:p-8 bg-white hover:shadow-xl transition-all duration-300 relative ${!hasDetailPage ? 'cursor-default' : ''}`}
                  >
                    <article>
                      {/* View Details Indicator - Only show if detail page exists */}
                      {hasDetailPage && (
                        <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                          <div className="flex items-center gap-1.5 text-teal-600">
                            <span className="text-xs font-medium hidden sm:inline">View Details</span>
                            <ArrowRight size={14} className="sm:group-hover:translate-x-1 transition-transform duration-300" />
                          </div>
                        </div>
                      )}

                      {/* Number Indicator */}
                      <div className="flex items-start gap-3 sm:gap-4 lg:gap-6">
                        <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-[#E5E5E5] ${hasDetailPage ? 'group-hover:border-teal-600' : ''} flex items-center justify-center transition-colors duration-300`}>
                          <span className={`text-base sm:text-lg font-bold text-[#6B6B6B] ${hasDetailPage ? 'group-hover:text-teal-600' : ''} transition-colors duration-300`}>
                            {String(index + 1).padStart(2, '0')}
                          </span>
                        </div>

                        <div className={`flex-1 ${hasDetailPage ? 'sm:pr-16' : ''} min-w-0`}>
                          {/* Project Title - Same as Professional Work */}
                          <h3 className={`font-serif text-xl sm:text-2xl lg:text-3xl text-[#1A1A1A] mb-2 sm:mb-3 ${hasDetailPage ? 'group-hover:text-teal-700' : ''} transition-colors duration-300 leading-tight`}>
                            {project.title}
                          </h3>

                          {/* Identity Label + Role + Company + Location */}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                            <span className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border font-medium ${getCategoryColor(project.category)}`}>
                              {project.category}
                            </span>
                            <span className="text-xs sm:text-sm text-[#4A4A4A] break-words">
                              {project.role === project.organization ? project.organization : `${project.role} · ${project.organization}`}
                            </span>
                            {project.location && (
                              <>
                                <span className="text-[#999999] hidden sm:inline">·</span>
                                <span className="text-xs sm:text-sm text-[#999999] hidden sm:inline">
                                  {project.location}
                                </span>
                              </>
                            )}
                            <span className="text-[#999999] hidden sm:inline">·</span>
                            <span className="text-xs sm:text-sm text-[#999999] hidden sm:inline">
                              {project.period}
                            </span>
                          </div>

                          {/* Summary */}
                          <p className="text-sm sm:text-base text-[#6B6B6B] leading-relaxed mb-4 sm:mb-6 line-clamp-2">
                            {project.description}
                          </p>

                          {/* Tags */}
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {project.tags.map((tag, tagIndex) => (
                              <span
                                key={tagIndex}
                                className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-50 text-[#6B6B6B] rounded-md border border-gray-200 ${hasDetailPage ? 'group-hover:border-teal-100 group-hover:bg-teal-50/30' : ''} transition-colors duration-300`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </article>
                  </CardWrapper>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Technical Capabilities Section - Enhanced with Better Visibility */}
      <section id="skills" className="py-20 px-6 lg:px-12 bg-gradient-to-b from-gray-50/50 to-white border-y border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="mb-12">
            <h2 className="font-serif text-3xl lg:text-4xl text-[#1A1A1A] mb-4 font-bold">
              Technical Capabilities
            </h2>
            <p className="text-base text-[#6B6B6B] mb-8">
              Technologies I use to build and operate an Applied AI System
            </p>
          </div>

          {/* Top Row: AI/ML Systems + Frameworks - Enhanced Visibility */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* AI/ML Systems */}
            <div className="border-2 border-[#E5E5E5] rounded-xl p-6 bg-white hover:border-teal-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-teal-50 rounded-lg group-hover:bg-teal-100 transition-colors">
                  <Brain className="text-teal-600" size={24} />
                </div>
                <h3 className="font-serif text-xl text-[#1A1A1A] font-semibold">
                  {technicalCapabilities.aiMlSystems.title}
                </h3>
              </div>
              
              <p className="text-xs text-[#6B6B6B] mb-5 italic">
                {technicalCapabilities.aiMlSystems.description}
              </p>

              <ul className="space-y-2">
                {technicalCapabilities.aiMlSystems.capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-teal-600 mt-0.5 flex-shrink-0 text-sm font-bold">•</span>
                    <span className="text-xs text-[#3A3A3A] leading-relaxed">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Frameworks & Protocols - Enhanced with Hover */}
            <div className="border-2 border-[#E5E5E5] rounded-xl p-6 bg-white hover:border-teal-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-teal-50 rounded-lg group-hover:bg-teal-100 transition-colors">
                  <Layers className="text-teal-600" size={24} />
                </div>
                <h3 className="font-serif text-xl text-[#1A1A1A] font-semibold">
                  {technicalCapabilities.frameworksProtocols.title}
                </h3>
              </div>
              
              <p className="text-xs text-[#6B6B6B] mb-5 italic">
                {technicalCapabilities.frameworksProtocols.purpose}
              </p>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/langchain-1.svg" 
                    alt="LangChain"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">LangChain</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/llamaindex-color.svg" 
                    alt="LlamaIndex"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">LlamaIndex</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <div className="h-10 flex items-center justify-center">
                    <img 
                      src="/assets/logos/pytorch-2.svg" 
                      alt="PyTorch"
                      className="w-[72px] h-[72px] object-contain"
                    />
                  </div>
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">PyTorch</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/tensorflow-2.svg" 
                    alt="TensorFlow"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">TensorFlow</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/Agent2Agent.png" 
                    alt="Agent-to-Agent"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center leading-tight">A2A Protocol</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/workflow_orchestrations.png" 
                    alt="Workflow Orchestration"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center leading-tight">Workflow Orchestration</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row: 4 Columns with Enhanced Hover Effects */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Languages */}
            <div className="border-2 border-[#E5E5E5] rounded-xl p-5 bg-white hover:border-teal-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-1.5 bg-teal-50 rounded-lg group-hover:bg-teal-100 transition-colors">
                  <Code className="text-teal-600" size={18} />
                </div>
                <h3 className="text-base font-bold text-[#1A1A1A]">
                  {technicalCapabilities.languages.title}
                </h3>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/python.png" 
                    alt="Python"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">Python</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/typescript.png" 
                    alt="TypeScript"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">TypeScript</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/js.png" 
                    alt="JavaScript"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">JavaScript</span>
                </div>
              </div>
            </div>

            {/* Backend & APIs */}
            <div className="border-2 border-[#E5E5E5] rounded-xl p-5 bg-white hover:border-teal-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-1.5 bg-teal-50 rounded-lg group-hover:bg-teal-100 transition-colors">
                  <Server className="text-teal-600" size={18} />
                </div>
                <h3 className="text-base font-bold text-[#1A1A1A]">
                  {technicalCapabilities.backendApis.title}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/fastapi-1.svg" 
                    alt="FastAPI"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">FastAPI</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/nodejs-2.svg" 
                    alt="Node.js"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">Node.js</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/rest_api.png" 
                    alt="REST APIs"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center leading-tight">REST APIs</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <Layers className="text-teal-600" size={36} />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center leading-tight">Async</span>
                </div>
              </div>
            </div>

            {/* Data & Storage */}
            <div className="border-2 border-[#E5E5E5] rounded-xl p-5 bg-white hover:border-teal-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-1.5 bg-teal-50 rounded-lg group-hover:bg-teal-100 transition-colors">
                  <HardDrive className="text-teal-600" size={18} />
                </div>
                <h3 className="text-base font-bold text-[#1A1A1A]">
                  {technicalCapabilities.dataStorage.title}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/postgresql.svg" 
                    alt="PostgreSQL"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">PostgreSQL</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/mongodb-icon-1-1.svg" 
                    alt="MongoDB"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">MongoDB</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/vector_db.png" 
                    alt="Vector DB"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center leading-tight">Vector DB</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/qdrant-icon-seeklogo.png" 
                    alt="Qdrant"
                    className="w-9 h-9 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">Qdrant</span>
                </div>
              </div>
            </div>

            {/* Infrastructure & Cloud */}
            <div className="border-2 border-[#E5E5E5] rounded-xl p-5 bg-white hover:border-teal-300 hover:shadow-lg transition-all duration-300 group">
              <div className="flex items-center gap-2 mb-5">
                <div className="p-1.5 bg-teal-50 rounded-lg group-hover:bg-teal-100 transition-colors">
                  <Cloud className="text-teal-600" size={18} />
                </div>
                <h3 className="text-base font-bold text-[#1A1A1A]">
                  {technicalCapabilities.infrastructureCloud.title}
                </h3>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/docker.png" 
                    alt="Docker"
                    className="w-8 h-8 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">Docker</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/aws-2.svg" 
                    alt="AWS"
                    className="w-8 h-8 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">AWS</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/azure-2.svg" 
                    alt="Azure"
                    className="w-8 h-8 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">Azure</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/google-cloud-2.svg" 
                    alt="GCP"
                    className="w-8 h-8 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center">GCP</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <GitBranch className="text-teal-600" size={32} />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center leading-tight">CI/CD</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer">
                  <img 
                    src="/assets/logos/monitor.png" 
                    alt="Monitoring"
                    className="w-8 h-8 object-contain"
                  />
                  <span className="text-[11px] font-semibold text-[#2A2A2A] text-center leading-tight">Monitoring</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
